import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Card, PivotCard } from "..";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentPivot, getTermFromAggs } from "@radarly/models/search";
import { isPivotCardDataviz } from "../dataviz";
import { isPivotCard } from "../is";
import { PivotCardTerm, getTermFromCard } from "../term";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface PivotCardFactoryOptions {
  term?: PivotCardTerm;
  pivot?: PivotCardTerm;
  from?: AbstractDate;
  metric?: Metric;
  to?: AbstractDate;
  clause?: Clause;
}

export class PivotCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: PivotCardFactoryOptions) {
    const factory = new PivotCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: PivotCardFactoryOptions) {
    const factory = new PivotCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      term: factory.getTermFromCard(card, input) as PivotCardTerm,
      pivot: factory.getPivotTermFromCard(card, input) as PivotCardTerm,
      metric: factory.getMetricFromCardAndInput(card, input),
    });
  }
  public getPivotTermFromCard(card: Card, options: { pivot?: Facet }) {
    if (options && options.pivot) {
      return options.pivot;
    } else {
      if (isPivotCard(card)) {
        const term = getTermFromCard(card);
        const fragment = card.fragments[0];
        return getTermFromAggs(fragment.request.aggs[term].aggs) as Facet;
      } else {
        return null;
      }
    }
  }
  private getFragments(input?: PivotCardFactoryOptions): [SearchFragmentPivot] {
    return [this.getFragment(input)];
  }
  private getFragment(input?: PivotCardFactoryOptions): SearchFragmentPivot {
    const metric = input?.metric || Metric.Doc;
    const term = input?.term || Facet.Platforms;
    const pivot = input?.pivot || Facet.Tones;
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          [term]: {
            metrics: [metric],
            aggs: {
              [pivot === term ? Facet.Platforms : pivot]: {
                metrics: [metric],
              },
            },
          },
        },
      },
    };
  }
}
export class PivotCardFactory
  implements ICardFactory<PivotCard, PivotCardFactoryOptions>
{
  public static getInstance() {
    return new PivotCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: PivotCardFactoryOptions & {
      dataviz?: PivotCard["dataviz"];
    }
  ) {
    return CardFactory.getInstance<PivotCard>().createCard(
      workspaceId,
      projectFocuses,
      "pivot",
      card.title,
      PivotCardFragmentsFactory.createFromCard(card, input),
      {
        ...this.createDataviz(input?.dataviz || card.dataviz),
        ...{ sort: this.getDefaultSort(input.pivot) },
      }
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: PivotCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<PivotCard>().createCard(
      workspaceId,
      projectFocuses,
      "pivot",
      title,
      PivotCardFragmentsFactory.create(input),
      {
        ...this.createDataviz(
          input?.fullDataviz ??
            getDefaultDataviz(
              input?.dataviz || DatavizName.HorizontalStackedBarChart
            )
        ),
        ...{ sort: this.getDefaultSort(input?.pivot) },
      }
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isPivotCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        DatavizName.HorizontalStackedBarChart
      ) as PivotCard["dataviz"];
    }
  }
  private getDefaultSort(pivot?: PivotCardTerm) {
    return pivot && pivot === Facet.Tones ? "positive" : "default";
  }
}
